// CatalogPanel — Rahmen des Karten-Katalogs im Deck-Editor: zweite Seitenspalte auf der
// Gegenseite der Kommandobrücke. Trägt Seite, Breite, Trennlinie und Stufe 2; der Inhalt
// kommt unverändert aus `CardCatalog`, das ✕ steht in dessen Reiterkopf.
import React, { useCallback, useRef, useState } from 'react';
import { MAX_CATALOG_WIDTH, MIN_CATALOG_WIDTH } from '../../../hooks/useEditorPreferences';
import CardCatalog from './CardCatalog';
import './CatalogPanel.css';

/**
 * Anteil der Fensterbreite, ab dem die Trennlinie in Stufe 2 umschlägt: der Katalog
 * nimmt die ganze Fläche, die Playmat bleibt gemountet und schrumpft auf null.
 */
const STAGE_TWO_RATIO = 0.85;

/** Schrittweite der Pfeiltasten auf der Trennlinie. */
const KEYBOARD_STEP_PX = 16;

/**
 * Seitenspalte des Karten-Katalogs.
 *
 *   <CatalogPanel
 *     side="right" deckId={42}
 *     width={380} onWidthChange={setCatalogWidth}
 *     stageTwo={stageTwo} onStageTwoChange={setStageTwo}
 *     activeTab={tab} onTabChange={setTab}
 *     onClose={closeCatalog}
 *     onAddCard={handleCatalogAdd}
 *   />
 *
 * Props:
 *   side        — 'left' | 'right'; die Gegenseite der Brücke, gesetzt vom Editor
 *   width       — Breite in Pixel (Präferenz, 305–900)
 *   onWidthChange — (px) => void, während des Ziehens laufend gerufen
 *   stageTwo    — true, solange der Katalog die ganze Fläche nimmt
 *   onStageTwoChange — (bool) => void; der Editor legt daraufhin die Playmat auf null
 *   onClose     — wird an `CardCatalog` durchgereicht, das daraus das ✕ in seinem Reiterkopf zeigt
 *   alle übrigen Props gehen unverändert an `CardCatalog` (Vertrag dort)
 *
 * Die Trennlinie ist auch über die Tastatur bedienbar: Tab führt auf sie, die Pfeiltasten
 * verschieben sie um 16 px, Home/End setzen sie auf die Grenzwerte. Stufe 2 bleibt dem
 * Ziehen vorbehalten — sie ist kein Breiten-Wert, aus dem ein nächster Schritt folgte.
 *
 * Der Schließ-Knopf steht im Reiterkopf des Katalogs (`onClose` wird durchgereicht); das
 * Panel hat keine eigene Kopfzeile.
 *
 * Zugeklappt rendert der Editor dieses Bauteil gar nicht — es gibt keine Rest-Leiste.
 * In Stufe 2 bleibt die gespeicherte Breite auf ihrem letzten normalen Wert stehen:
 * Stufe 2 ist ein Zustand der Sitzung, kein Breiten-Wert, und beim Zurückziehen soll
 * die Spalte wieder die Breite haben, die man ihr gegeben hat. Das Ziehen selbst geht
 * dennoch von der gerenderten Breite aus, damit die Kante auch beim Verlassen von Stufe 2
 * dem Zeiger folgt.
 */
export default function CatalogPanel({
  side = 'right',
  width = 380,
  onWidthChange,
  stageTwo = false,
  onStageTwoChange,
  onClose,
  ...catalogProps
}) {
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef(null);
  const panelRef = useRef(null);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    // Ausgangsbreite ist die GERENDERTE, nicht die gespeicherte: in Stufe 2 ist die Spalte
    // fast fensterbreit, während die Präferenz noch ihren letzten normalen Wert trägt. Mit
    // dem Präferenz-Wert spränge die Kante beim ersten Millimeter dorthin zurück, statt der
    // Bewegung zu folgen.
    const gerendert = panelRef.current?.getBoundingClientRect().width;
    dragStartRef.current = { x: event.clientX, width: gerendert || width };
    setDragging(true);
  }, [width]);

  const handlePointerMove = useCallback((event) => {
    const start = dragStartRef.current;
    if (!start) return;
    // Die Spalte wächst zur Fenstermitte hin: rechts liegend wird sie breiter, wenn
    // der Zeiger nach links geht — links liegend umgekehrt.
    const delta = event.clientX - start.x;
    const roh = side === 'right' ? start.width - delta : start.width + delta;

    if (roh > window.innerWidth * STAGE_TWO_RATIO) {
      onStageTwoChange?.(true);
      return;
    }
    onStageTwoChange?.(false);
    onWidthChange?.(Math.max(MIN_CATALOG_WIDTH, Math.min(MAX_CATALOG_WIDTH, roh)));
  }, [side, onWidthChange, onStageTwoChange]);

  // Tastatur-Bedienung derselben Kante: die Pfeile bewegen sie in Richtung ihrer Taste,
  // die Spalte wächst also spiegelbildlich zur Seite, auf der sie liegt — wie beim Ziehen.
  const handleKeyDown = useCallback((event) => {
    const zurSpalteHin = side === 'right' ? 'ArrowLeft' : 'ArrowRight';
    const vonDerSpalteWeg = side === 'right' ? 'ArrowRight' : 'ArrowLeft';
    // Ausgangsbreite ist wie beim Ziehen die GERENDERTE: in Stufe 2 trägt die Präferenz
    // noch ihren letzten normalen Wert, und schnelle Tastenwiederholungen rechnen sonst
    // mehrfach von derselben Breite aus, statt sich zu addieren.
    const basis = panelRef.current?.getBoundingClientRect().width || width;
    let ziel = null;
    if (event.key === zurSpalteHin) ziel = basis + KEYBOARD_STEP_PX;
    else if (event.key === vonDerSpalteWeg) ziel = basis - KEYBOARD_STEP_PX;
    else if (event.key === 'Home') ziel = MIN_CATALOG_WIDTH;
    else if (event.key === 'End') ziel = MAX_CATALOG_WIDTH;
    if (ziel == null) return;
    event.preventDefault();
    // Stufe 2 ist über die Tastatur nicht erreichbar: sie hat keinen Breiten-Wert, aus dem
    // sich der nächste Schritt ergäbe — der Weg dorthin bleibt das Ziehen.
    onStageTwoChange?.(false);
    onWidthChange?.(Math.max(MIN_CATALOG_WIDTH, Math.min(MAX_CATALOG_WIDTH, ziel)));
  }, [side, width, onWidthChange, onStageTwoChange]);

  const handlePointerUp = useCallback((event) => {
    if (!dragStartRef.current) return;
    dragStartRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return (
    <aside
      ref={panelRef}
      className={`catalog-panel catalog-panel--${side}${stageTwo ? ' catalog-panel--stage-two' : ''}${dragging ? ' catalog-panel--dragging' : ''}`}
      style={stageTwo ? undefined : { width: `min(${width}px, calc(100vw - var(--catalog-window-reserve)))` }}
    >
      <div
        className="catalog-panel__resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Katalogbreite ändern"
        tabIndex={0}
        aria-valuenow={Math.round(width)}
        aria-valuemin={MIN_CATALOG_WIDTH}
        aria-valuemax={MAX_CATALOG_WIDTH}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <span className="catalog-panel__grip" aria-hidden />
      </div>
      <div className="catalog-panel__body">
        <CardCatalog {...catalogProps} onClose={onClose} />
      </div>
    </aside>
  );
}
